import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LifeBuoy, RefreshCw, Mail, Bug, HelpCircle, Lightbulb, CreditCard,
  ChevronRight, Loader2, MessageCircle, ExternalLink, Send,
} from 'lucide-react';
import { API } from '@/App';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'closed', label: 'Closed' },
];

const STATUS_LABELS = {
  open: { label: 'Open', cls: 'bg-[#EFF6FF] border-[#2563FF]/40 text-[#3B82F6]' },
  in_progress: { label: 'In progress', cls: 'bg-yellow-950/40 border-yellow-500/40 text-yellow-300' },
  closed: { label: 'Closed', cls: 'bg-gray-900/40 border-gray-600/40 text-[#64748B]' },
};

const CATEGORY_ICON = {
  bug: Bug,
  question: HelpCircle,
  feature: Lightbulb,
  billing: CreditCard,
  other: HelpCircle,
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function AdminSupportPanel() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, closed: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // ticket detail
  const [chatMessages, setChatMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [reply, setReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const fetchTickets = async (status = statusFilter) => {
    setLoading(true);
    try {
      const url = `${API}/admin/support/tickets${status ? `?status=${status}` : ''}`;
      const { data } = await axios.get(url);
      setTickets(data.tickets || []);
      setCounts(data.counts || { open: 0, in_progress: 0, closed: 0 });
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []); // eslint-disable-line

  const onFilterChange = (val) => {
    setStatusFilter(val);
    fetchTickets(val);
  };

  const openTicket = async (ticket) => {
    setSelected(ticket);
    setReply('');
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/support/tickets/${ticket.id}`);
      setSelected(data.ticket);
      setChatMessages(data.chat_messages || []);
    } catch {
      toast.error('Failed to load ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const sendReply = async () => {
    if (!selected || sendingReply) return;
    const content = reply.trim();
    if (!content) return;
    setSendingReply(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/support/tickets/${selected.id}/reply`,
        { content },
      );
      setSelected(data.ticket);
      setTickets((prev) =>
        prev.map((t) => (t.id === data.ticket.id ? data.ticket : t)),
      );
      setReply('');
      fetchTickets(statusFilter);
      toast.success('Reply sent — user has been emailed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const updateTicket = async (updates) => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/support/tickets/${selected.id}`,
        updates,
      );
      setSelected(data.ticket);
      setTickets((prev) =>
        prev.map((t) => (t.id === data.ticket.id ? data.ticket : t)),
      );
      // Refresh counts
      fetchTickets(statusFilter);
      toast.success('Ticket updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="admin-support-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
        <div>
          <h3 className="font-heading text-lg text-[#0F172A] flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-[#2563FF]" />
            SUPPORT TICKETS
          </h3>
          <p className="font-mono text-xs text-[#94A3B8] mt-1">
            Open: {counts.open} · In progress: {counts.in_progress} · Closed: {counts.closed}
          </p>
        </div>
        <button
          onClick={() => fetchTickets(statusFilter)}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E2E8F0] hover:border-[#2563FF]/40 text-[#64748B] hover:text-[#0F172A] text-xs font-mono uppercase"
          data-testid="admin-support-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 px-4 py-3 border-b border-[#E2E8F0] overflow-x-auto">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => onFilterChange(f.value)}
            className={`px-3 py-1 text-xs font-mono uppercase tracking-wider border whitespace-nowrap ${
              statusFilter === f.value
                ? 'bg-[#EFF6FF] text-[#3B82F6] border-[#2563FF]/40'
                : 'border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]'
            }`}
            data-testid={`admin-support-filter-${f.value || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Layout: list + detail */}
      <div className="grid lg:grid-cols-[1fr_1.4fr]">
        {/* List */}
        <div className="border-r border-[#E2E8F0] max-h-[640px] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-[#64748B]">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center text-[#64748B]">
              <MessageCircle className="w-10 h-10 mx-auto mb-3 text-[#1E3A8A]" />
              <p className="font-body text-sm">No tickets {statusFilter ? `with status "${statusFilter}"` : 'yet'}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {tickets.map((t) => {
                const Icon = CATEGORY_ICON[t.category] || HelpCircle;
                const sLabel = STATUS_LABELS[t.status] || STATUS_LABELS.open;
                const isActive = selected?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => openTicket(t)}
                    className={`w-full text-left p-4 flex items-start gap-3 transition-colors ${
                      isActive ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'
                    }`}
                    data-testid={`admin-ticket-row-${t.id}`}
                  >
                    <Icon className="w-4 h-4 text-[#2563FF] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body text-sm text-[#0F172A] truncate">
                          {t.subject}
                        </span>
                        <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${sLabel.cls}`}>
                          {sLabel.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-[11px] text-[#64748B] truncate">
                          {t.name} · {t.email}
                        </span>
                      </div>
                      <p className="font-mono text-[10px] text-[#94A3B8] mt-1">
                        {fmtDate(t.created_at)}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#475569] shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="max-h-[640px] overflow-y-auto" data-testid="admin-ticket-detail">
          {!selected ? (
            <div className="p-12 text-center text-[#94A3B8]">
              <LifeBuoy className="w-12 h-12 mx-auto mb-3 text-[#1E3A8A]" />
              <p className="font-body text-sm">Select a ticket to view details</p>
            </div>
          ) : detailLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#2563FF]" />
            </div>
          ) : (
            <div className="p-5 space-y-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">
                  Subject · {selected.category}
                </p>
                <h4 className="font-heading text-lg text-[#0F172A]">{selected.subject}</h4>
                <div className="flex flex-wrap gap-3 mt-2 font-mono text-[11px] text-[#64748B]">
                  <span>From: <span className="text-[#0F172A]">{selected.name}</span></span>
                  <a href={`mailto:${selected.email}`} className="text-[#2563FF] hover:text-[#3B82F6] inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {selected.email}
                  </a>
                  <span>{fmtDate(selected.created_at)}</span>
                </div>
              </div>

              {/* Message thread */}
              <div className="space-y-2" data-testid="admin-ticket-thread">
                {(selected.messages || [{ role: 'user', author: selected.name, content: selected.message, created_at: selected.created_at }]).map((m, i) => {
                  const isAdmin = m.role === 'admin';
                  return (
                    <div
                      key={m.id || i}
                      className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                      data-testid={`admin-thread-msg-${m.role}`}
                    >
                      <div className="max-w-[85%]">
                        <div className={`px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                          isAdmin
                            ? 'bg-[#2563FF] text-[#0F172A]'
                            : 'bg-white border border-[#E2E8F0] text-[#0F172A]'
                        }`}>
                          {m.content}
                        </div>
                        <p className={`font-mono text-[10px] text-[#94A3B8] mt-1 ${isAdmin ? 'text-right' : ''}`}>
                          {isAdmin ? 'CRATELAB Team' : (m.author || selected.name || 'User')} · {fmtDate(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.screenshot_url && (
                <a
                  href={selected.screenshot_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-[#2563FF] hover:text-[#3B82F6]"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  View screenshot
                </a>
              )}

              {/* AI chat history */}
              {chatMessages.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                    Linked AI chat ({chatMessages.length} messages)
                  </p>
                  <div className="border border-[#E2E8F0] max-h-64 overflow-y-auto p-3 space-y-2 bg-[#F5F7FB]">
                    {chatMessages.map((m) => (
                      <div
                        key={m.id}
                        className={`text-xs whitespace-pre-wrap p-2 ${
                          m.role === 'user'
                            ? 'bg-[#EFF6FF] text-[#1E40AF]'
                            : 'bg-white text-[#334155] border border-[#E2E8F0]'
                        }`}
                      >
                        <span className="font-mono text-[10px] uppercase tracking-wider opacity-50 mr-2">
                          {m.role}
                        </span>
                        {m.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status controls */}
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                  Status
                </p>
                <div className="flex gap-2">
                  {['open', 'in_progress', 'closed'].map((s) => (
                    <button
                      key={s}
                      disabled={savingNotes || selected.status === s}
                      onClick={() => updateTicket({ status: s })}
                      className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                        selected.status === s
                          ? `${STATUS_LABELS[s].cls} cursor-default`
                          : 'border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]'
                      }`}
                      data-testid={`admin-ticket-status-${s}`}
                    >
                      {STATUS_LABELS[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin notes */}
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                  Internal notes
                </p>
                <textarea
                  defaultValue={selected.admin_notes || ''}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (selected.admin_notes || '').trim()) {
                      updateTicket({ admin_notes: v });
                    }
                  }}
                  rows={3}
                  className="w-full bg-[#F5F7FB] border border-[#E2E8F0] focus:border-[#2563FF]/40 outline-none px-3 py-2 text-sm text-[#0F172A] resize-none"
                  placeholder="Notes for the team (saved when you click outside)…"
                  data-testid="admin-ticket-notes"
                />
              </div>

              {/* Reply box */}
              <div data-testid="admin-reply-box">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">
                  Reply to user
                </p>
                <div className="border border-[#E2E8F0] bg-[#F5F7FB]">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={
                      selected.status === 'closed'
                        ? 'Reopen the ticket to send a reply…'
                        : `Write a reply to ${selected.name || selected.email}…`
                    }
                    rows={4}
                    disabled={selected.status === 'closed'}
                    className="w-full bg-transparent border-0 outline-none px-3 py-2 text-sm text-[#0F172A] placeholder-gray-600 resize-none disabled:opacity-40"
                    data-testid="admin-reply-input"
                    maxLength={5000}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply();
                    }}
                  />
                  <div className="flex items-center justify-between border-t border-[#E2E8F0] px-3 py-2">
                    <p className="font-mono text-[10px] text-[#475569]">
                      ⌘/Ctrl + Enter · {reply.length}/5000 · user gets email + portal link
                    </p>
                    <button
                      onClick={sendReply}
                      disabled={!reply.trim() || sendingReply || selected.status === 'closed'}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#2563FF] hover:bg-[#2563FF] disabled:bg-[#EFF6FF] disabled:text-[#94A3B8] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
                      data-testid="admin-reply-send"
                    >
                      {sendingReply ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                      Send reply
                    </button>
                  </div>
                </div>
              </div>

              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                className="inline-flex items-center gap-2 text-xs font-mono text-[#64748B] hover:text-[#0F172A]"
                data-testid="admin-ticket-reply-email"
              >
                <Mail className="w-3.5 h-3.5" />
                Or open in your mail client
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
